import { z } from "zod"

export const StatusConversationOriginType = z.enum(["marketing", "utility", "authentication"])
export const StatusEnum = z.enum(["sent", "delivered", "read", "failed"])

const WhatsappIdentitySchema = z
  .string()
  .transform((value) => (value ? value : undefined))
  .optional()

export const WhatsappWebhookContactSchema = z.object({
  profile: z
    .object({
      name: z.string().optional(),
      username: z.string().optional(),
    })
    .nullish(),
  // `wa_id` is the standard E.164 phone identifier. The `internal-permut` integration variant
  // sends `user_id` (e.g. "CL.xxx") and omits `wa_id`, so both fields are optional.
  wa_id: WhatsappIdentitySchema,
  user_id: WhatsappIdentitySchema,
})

export const WhatsappMessageContext = z
  .object({
    from: WhatsappIdentitySchema,
    id: z.string().optional(),
    forwarded: z.boolean().optional(),
  })
  .nullish()

const WhatsappWebhookMessageBaseSchema = z.object({
  from: WhatsappIdentitySchema,
  from_user_id: WhatsappIdentitySchema,
  id: z.string(),
  timestamp: z.string(),
})

const ProcessableWhatsappWebhookMessageSchema = z
  .union([
    WhatsappWebhookMessageBaseSchema.extend({
      text: z.object({
        body: z.string(),
      }),
      type: z.literal("text"),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("audio"),
      audio: z.object({
        caption: z.string().optional(),
        mime_type: z.string(),
        id: z.string(),
        sha256: z.string(),
      }),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("image"),
      image: z.object({
        caption: z.string().optional(),
        mime_type: z.string(),
        sha256: z.string(),
        id: z.string(),
      }),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("sticker"),
      sticker: z.object({
        mime_type: z.string(),
        sha256: z.string(),
        id: z.string(),
        animated: z.boolean(),
      }),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("video"),
      video: z.object({
        caption: z.string().optional(),
        mime_type: z.string(),
        sha256: z.string(),
        id: z.string(),
      }),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("reaction"),
      reaction: z.object({
        message_id: z.string(),
        emoji: z.string().optional(),
      }),
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("unsupported"),
      errors: z.array(z.object({})),
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("system"),
      system: z.object({
        body: z.string(),
        wa_id: z.string(),
        type: z.enum(["user_changed_number"]),
      }),
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("location"),
      location: z.object({
        latitude: z.number(),
        longitude: z.number(),
      }),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("contacts"),
      contacts: z.array(
        z.object({
          name: z.object({
            first_name: z.string().nullish(),
            last_name: z.string().nullish(),
            formatted_name: z.string(),
          }),
          phones: z
            .array(
              z.object({
                phone: z.string(),
                wa_id: WhatsappIdentitySchema,
                type: z.string().optional(),
              }),
            )
            .optional(),
        }),
      ),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("document"),
      document: z.object({
        filename: z.string(),
        mime_type: z.string(),
        sha256: z.string(),
        id: z.string(),
      }),
      context: WhatsappMessageContext,
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("interactive"),
      interactive: z.union([
        z.object({
          type: z.literal("button_reply"),
          button_reply: z.object({
            id: z.string(),
            title: z.string(),
          }),
        }),
        z.object({
          type: z.literal("list_reply"),
          list_reply: z.object({
            id: z.string(),
            title: z.string(),
            description: z.string().optional(),
          }),
        }),
      ]),
    }),
    WhatsappWebhookMessageBaseSchema.extend({
      type: z.literal("button"),
      button: z.object({
        payload: z.string(),
        text: z.string(),
      }),
    }),
  ])
  .refine(({ from, from_user_id }) => from || from_user_id, {
    message: "Either from or from_user_id is required",
  })

export const WhatsappWebhookMessageSchema = ProcessableWhatsappWebhookMessageSchema

const WhatsappStatusErrorSchema = z.object({
  code: z.number(),
  title: z.string(),
  message: z.string(),
  error_data: z
    .object({
      details: z.string().optional(),
    })
    .optional(),
  href: z.string().optional(),
})

export const WhatsappWebhookPricingSchema = z.object({
  billable: z.boolean().optional(),
  pricing_model: z.string().optional(),
  category: z.string().optional(),
  type: z.string().optional(),
})

export const WhatsappWebhookStatusSchema = z
  .object({
    id: z.string(),
    status: StatusEnum,
    timestamp: z.string(),
    recipient_id: WhatsappIdentitySchema,
    // Sent by the `internal-permut` integration variant alongside `recipient_id`.
    recipient_user_id: WhatsappIdentitySchema,
    errors: z.array(WhatsappStatusErrorSchema).optional(),
    pricing: WhatsappWebhookPricingSchema.optional(),
  })
  .refine(({ recipient_id, recipient_user_id }) => recipient_id || recipient_user_id, {
    message: "Either recipient_id or recipient_user_id is required",
  })

export const WhatsappWebhookMessagesBodySchema = z.object({
  field: z.literal("messages"),
  value: z.object({
    messaging_product: z.string(),
    metadata: z.object({
      display_phone_number: z.string(),
      phone_number_id: z.string(),
    }),
    contacts: z.array(WhatsappWebhookContactSchema).optional(),
    messages: z.array(WhatsappWebhookMessageSchema).optional(),
    statuses: z.array(WhatsappWebhookStatusSchema).optional(),
  }),
})

export const WhatsappMessageStatusUpdateEnum = z.enum([
  "APPROVED",
  "ARCHIVED",
  "DELETED",
  "DISABLED",
  "FLAGGED",
  "IN_APPEAL",
  "LIMIT_EXCEEDED",
  "LOCKED",
  "PAUSED",
  "PENDING",
  "REINSTATED",
  "PENDING_DELETION",
  "REJECTED",
])

const WhatsappMessageTemplateIdSchema = z.union([z.string(), z.number()]).transform(String)

export const WhatsappWebhookMessageTemplateStatusUpdateBodySchema = z.object({
  field: z.literal("message_template_status_update"),
  value: z.object({
    event: WhatsappMessageStatusUpdateEnum,
    message_template_id: WhatsappMessageTemplateIdSchema,
    message_template_name: z.string(),
    reason: z.string().nullable(),
    other_info: z
      .object({
        title: z.string().nullish(),
        description: z.string().nullish(),
      })
      .nullish(),
  }),
})

export const WhatsappWebhookTemplateCategoryUpdateBodySchema = z.object({
  field: z.literal("template_category_update"),
  value: z.object({
    message_template_id: WhatsappMessageTemplateIdSchema,
    message_template_name: z.string(),
    message_template_language: z.string(),
    previous_category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]).optional(),
    new_category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]),
  }),
})

export const WhatsappWebhookAccountUpdateSchema = z.object({
  field: z.literal("account_update"),
  value: z
    .object({
      event: z.string(),
      waba_info: z
        .object({
          waba_id: z.string().optional(),
          phone_number_id: z.string().optional(),
        })
        .passthrough()
        .optional(),
      phone_number_id: z.string().optional(),
      reason: z.string().optional(),
    })
    .passthrough()
    .optional(),
})

const WhatsappCoexistenceMediaSchema = z.object({
  id: z.string().optional(),
  caption: z.string().optional(),
  filename: z.string().optional(),
})

export const WhatsappCoexistenceMessageSchema = z.object({
  id: z.string().min(1),
  from: z.string().min(1),
  to: z.string().optional(),
  timestamp: z.string().regex(/^\d+$/),
  type: z.string(),
  text: z.object({ body: z.string() }).optional(),
  image: WhatsappCoexistenceMediaSchema.optional(),
  audio: WhatsappCoexistenceMediaSchema.optional(),
  video: WhatsappCoexistenceMediaSchema.optional(),
  document: WhatsappCoexistenceMediaSchema.optional(),
  sticker: WhatsappCoexistenceMediaSchema.optional(),
  context: WhatsappMessageContext,
  reaction: z.object({ message_id: z.string(), emoji: z.string().optional() }).optional(),
  history_context: z.object({ status: z.string().optional() }).optional(),
})

const WhatsappCoexistenceMetadataSchema = z.object({
  display_phone_number: z.string(),
  phone_number_id: z.string(),
})

export const WhatsappWebhookEchoesSchema = z.object({
  field: z.literal("smb_message_echoes"),
  value: z.object({
    metadata: WhatsappCoexistenceMetadataSchema,
    message_echoes: z.array(WhatsappCoexistenceMessageSchema.extend({ to: z.string().min(1) })),
  }),
})

export const WhatsappWebhookHistorySchema = z.object({
  field: z.literal("history"),
  value: z.object({
    metadata: WhatsappCoexistenceMetadataSchema,
    history: z.array(
      z.object({
        metadata: z
          .object({
            phase: z.number().int(),
            chunk_order: z.number().int(),
            progress: z.number().min(0).max(100),
          })
          .optional(),
        threads: z
          .array(
            z.object({
              id: z.string(),
              messages: z.array(WhatsappCoexistenceMessageSchema),
            }),
          )
          .optional(),
        errors: z.array(z.object({ code: z.number(), message: z.string().optional() })).optional(),
      }),
    ),
  }),
})

export const WhatsappWebhookStateSyncSchema = z.object({
  field: z.literal("smb_app_state_sync"),
  value: z.object({
    metadata: WhatsappCoexistenceMetadataSchema,
    state_sync: z.array(
      z.object({
        type: z.literal("contact"),
        action: z.enum(["add", "remove"]),
        contact: z.object({
          phone_number: z.string(),
          full_name: z.string().optional(),
          first_name: z.string().optional(),
        }),
        metadata: z.object({ timestamp: z.string() }).optional(),
      }),
    ),
  }),
})

export const WhatsappWebhookAccountAlertValueSchema = z.object({
  alert_description: z.string(),
  alert_severity: z.string(),
  alert_status: z.string(),
  alert_type: z.string(),
  entity_id: z.string(),
  entity_type: z.string(),
  alert_id: z.string().optional(),
})

export const WhatsappWebhookAccountAlertsSchema = z.object({
  field: z.literal("account_alerts"),
  value: WhatsappWebhookAccountAlertValueSchema,
  id: z.string().optional(),
  time: z.number().optional(),
})

export const WhatsappWebhookBusinessCapabilityUpdateSchema = z.object({
  field: z.literal("business_capability_update"),
  value: z.object({
    max_daily_conversation_per_phone: z.number().optional(),
    max_phone_numbers_per_business: z.number().optional(),
    max_phone_numbers_per_waba: z.number().optional(),
    max_daily_conversations_per_business: z.union([z.string(), z.number()]).optional(),
  }),
})

export const WhatsappWebhookPhoneNumberNameUpdateSchema = z.object({
  field: z.literal("phone_number_name_update"),
  value: z.object({
    display_phone_number: z.string(),
    decision: z.string(),
    requested_verified_name: z.string(),
    rejection_reason: z.string(),
  }),
})

export const WhatsappWebhookSchema = z.object({
  object: z.string(),
  entry: z.array(
    z.object({
      id: z.string(),
      time: z.union([z.number(), z.string()]).optional(),
      changes: z.array(
        z.union([
          WhatsappWebhookAccountUpdateSchema,
          WhatsappWebhookAccountAlertsSchema,
          WhatsappWebhookMessagesBodySchema,
          WhatsappWebhookMessageTemplateStatusUpdateBodySchema,
          WhatsappWebhookTemplateCategoryUpdateBodySchema,
          WhatsappWebhookBusinessCapabilityUpdateSchema,
          WhatsappWebhookPhoneNumberNameUpdateSchema,
          WhatsappWebhookEchoesSchema,
          WhatsappWebhookHistorySchema,
          WhatsappWebhookStateSyncSchema,
        ]),
      ),
    }),
  ),
})

export const WhatsappWebhookParamsSchema = z.object({
  companySlug: z.string(),
})
